<template>
  <div class="landing-new airlines-page">
    <LandingHeader />
    <main class="airlines-page__content">
      <Hero
        variant="airlines"
        :hero-content="localized(heroContent, 'heroContent')"
        tracking-spot="airlines"
      />
      <WhatYouGain
        :icon-review="iconReview.gain"
        :content="localized(distributionContent, 'distributionContent')"
      />
      <EcosystemFlow
        :icon-review="iconReview.flow"
        :content="localized(workflowContent, 'workflowContent')"
      />
      <AirlineNetwork />
      <MetricsSection />
      <WhyDrct
        :icon-review="iconReview.why"
        :content="localized(whyContent, 'whyContent')"
        collapsible
      />
      <FinalCta :content="localized(finalCtaContent, 'finalCtaContent')" />
      <Faq :content="localized(faqContent, 'faqContent')" />
    </main>
    <LandingFooter />
  </div>
</template>

<script>
import { pageIconReview } from '../landing/page-icon-review.js';
import LandingHeader from '../landing/Header.vue';
import LandingFooter from '../landing/Footer.vue';
import Hero from '../landing/Hero.vue';
import WhatYouGain from '../landing/WhatYouGain.vue';
import EcosystemFlow from '../landing/EcosystemFlow.vue';
import AirlineNetwork from '../landing/AirlineNetwork.vue';
import MetricsSection from '../landing/MetricsSection.vue';
import WhyDrct from '../landing/WhyDrct.vue';
import FinalCta from '../landing/FinalCta.vue';
import Faq from '../landing/Faq.vue';
import '../landing/landing.scss';
import { airlinesTranslations } from './airlines-content.js';
import { airlinesPhosphorOptions } from './airlines-phosphor-icons.js';
import { mergeIataAgenciesContent } from './iata-agencies-content.js';

export default {
  components: {
    LandingHeader,
    LandingFooter,
    Hero,
    WhatYouGain,
    EcosystemFlow,
    AirlineNetwork,
    MetricsSection,
    WhyDrct,
    FinalCta,
    Faq,
  },
  data() {
    return {
      iconReview: pageIconReview.airlines,
      heroContent: {
        title: 'For Airlines',
        subtitle: 'One Platform to Distribute, Retail, and Grow.',
        description:
          'Adaptive Offer Orchestration. Adapt, enhance, and activate your airline offers through a unified airline distribution platform — while retaining full control over distribution and reaching IATA and non-IATA agencies, OTAs, and Super Apps.',
        ctaLabel: 'Book a Demo',
      },
      distributionContent: {
        heading: 'How DRCT Solves Challenges in Airline Distribution',
        description:
          'Airlines lose control of their offers across GDS and indirect channels. DRCT provides a unified layer to distribute, manage, and preserve offers consistently across every channel.',
        tabOneLabel: 'Distribution control',
        tabTwoLabel: 'NDC adoption',
        tabOneCards: [
          {
            title: 'A unified distribution layer',
            iconOptions: ['arrowsIn', ...airlinesPhosphorOptions.distribution],
            description:
              'DRCT provides a unified distribution layer where all airline offers are normalized and delivered through a single integration. Order management, reporting, and settlement are handled in one place, reducing operational complexity and eliminating fragmentation.',
          },
          {
            title: 'Offers preserved, end to end',
            iconOptions: ['ticket', ...airlinesPhosphorOptions.preservation],
            description:
              'DRCT preserves the full structure of your offer — including fares, ancillaries, bundles, and pricing logic — and delivers it consistently across all channels. This ensures your content is presented as intended and increases ancillary revenue per booking.',
          },
          {
            title: 'Consistent pricing and signals',
            iconOptions: ['sliders', ...airlinesPhosphorOptions.pricing],
            description:
              'DRCT maintains control over how offers are distributed and consumed, enabling consistent pricing, better personalization, and more accurate demand signals across all channels.',
          },
        ],
        tabTwoCards: [
          {
            title: 'NDC inside familiar workflows',
            iconOptions: ['linkSimple'],
            description:
              'DRCT delivers NDC content inside familiar agent workflows — including GDS-adjacent environments — eliminating the need to learn new systems. This accelerates NDC adoption without disrupting existing agent behavior.',
          },
          {
            title: 'Better unit economics',
            iconOptions: [airlinesPhosphorOptions.economics[2]], // 4: piggy-bank
            description:
              'DRCT enables direct airline-to-seller distribution and supports gradual volume shift from GDS to NDC channels. By reducing dependency on intermediaries, airlines improve unit economics while maintaining distribution reach.',
          },
        ],
      },
      workflowContent: {
        heading: 'How It Works',
        description:
          'DRCT connects your inventory to travel sellers through a single distribution layer — preserving offer structure and enabling full control over how your content is delivered and managed.',
        sourceEyebrow: 'Airline inventory',
        sourceHeading: 'Your offers',
        sourceItems: ['GDS content', 'NDC offers', 'Custom Scheme'],
        coreHeading: 'One unified layer',
        pipelineSteps: [
          { number: '01', text: 'Ingestion & normalization' },
          { number: '02', text: 'Offer assembly & enrichment' },
          { number: '03', text: 'Merchandising & pricing' },
          { number: '04', text: 'Order & servicing layer' },
        ],
        coreNoteText: 'One structure keeps offers and their logic under your control.',
        destEyebrow: 'Travel sellers',
        destHeading: 'Every channel',
        destItems: ['IATA agencies', 'Non-IATA agents', 'OTAs, TMCs'],
        steps: [
          {
            heading: 'Offer ingestion',
            description:
              'DRCT ingests your offers, normalizing them into a unified structure while preserving full offer logic and attributes.',
            icon: airlinesPhosphorOptions.ingestion[0], // 2: download-simple
          },
          {
            heading: 'Offer assembly and enrichment',
            description:
              'DRCT assembles and enriches offers — including virtual interlining, where itineraries are built from multiple airline offers to create routing options not available through a single carrier.',
            icon: airlinesPhosphorOptions.enrichment[0], // 2: intersect-three
          },
          {
            heading: 'Distribution to sellers',
            description:
              'Enriched offers are delivered to every connected seller — through DRCT Extension, Search, or API — intact and consistent across channels.',
            icon: airlinesPhosphorOptions.delivery[2], // 4: arrows-out
          },
          {
            heading: 'Order management',
            description:
              "All post-booking operations — changes, cancellations, refunds, ancillaries — are handled through DRCT's order management layer. Airlines retain full visibility and control, while agents can service bookings without additional airline involvement.",
            icon: airlinesPhosphorOptions.management[2], // 4: gear
          },
        ],
      },
      whyContent: {
        heading: 'Why Airlines Choose DRCT',
        description:
          "DRCT gives airlines something most airline distribution platforms can't: genuine coverage of every agent type, with the tools those agents actually need to sell and service NDC content confidently.",
        items: [
          {
            title: 'Faster NDC adoption',
            icon: airlinesPhosphorOptions.adoption[1], // 3: clock-countdown
            description:
              'Agents join NDC on their own terms — through the tool that fits their workflow. No forced migration.',
          },
          {
            title: 'Autonomous sales for agents',
            icon: airlinesPhosphorOptions.sales[1], // 3: hand-tap
            description:
              'Agents search, compare, and book your NDC content without needing to call your airline. Full self-service from fare selection to ticketing.',
          },
          {
            title: 'Post-booking self-service',
            icon: airlinesPhosphorOptions.servicing[1], // 3: arrows-clockwise
            description:
              'Changes, cancellations, refunds, and ancillary additions handled by agents directly. No manual queues. No airline intervention required.',
          },
          {
            title: 'Ancillaries and add-ons',
            icon: airlinesPhosphorOptions.ancillaries[0], // 2: suitcase-rolling
            description:
              'Agents can attach seats, baggage, and other services at the point of sale — increasing average booking value without extra effort on your side.',
          },
          {
            title: 'Full agent coverage across all workflows',
            icon: airlinesPhosphorOptions.coverage[1], // 3: users-three
            description:
              'Reach IATA and non-IATA agencies, OTAs, and TMCs: DRCT offers Extension (inside GDS), Search (UI), and API, allowing all three to work together and giving agents instant access to NDC content in the environment they already use.',
          },
          {
            title: '24/7 operational support',
            iconKey: 'heart',
            description:
              'Distribution issues, booking failures, and escalations — resolved by people who built the platform.',
          },
        ],
      },
      finalCtaContent: {
        title: 'Ready to modernize your distribution?',
        description:
          'Talk to the DRCT team about your current distribution setup and where you want to go.',
        ctaLabel: 'Book a Demo',
      },
      faqContent: {
        heading: 'FAQ',
        description: "If you didn't find an answer to your question, please ask our support.",
        items: [
          {
            question: 'Can airlines fully replace GDS with NDC?',
            answer:
              'Most airlines cannot fully replace GDS overnight. A gradual GDS-to-NDC migration is more realistic, allowing airlines to maintain agent coverage while increasing NDC volume. A hybrid approach ensures operational stability and avoids disruptions during the transition from GDS to NDC.',
          },
          {
            question: 'How does hybrid airline distribution (GDS + NDC) work in practice?',
            answer:
              'In hybrid airline distribution, GDS and NDC content coexist in one platform. DRCT enables mixing GDS and NDC content, providing a unified view for agents while simplifying operations. This approach ensures consistent content distribution across all sales channels.',
          },
          {
            question: 'How can airlines transition from GDS to NDC without losing agent coverage?',
            answer:
              'Airlines can transition using a phased approach with NDC integration layered on top of existing GDS workflows. DRCT ensures agents continue working in familiar environments while airlines scale NDC adoption without losing distribution reach.',
          },
          {
            question: 'How does DRCT help reduce airline distribution costs?',
            answer:
              'DRCT reduces costs by acting as an airline aggregator and shifting bookings from GDS to NDC and direct channels. This lowers reliance on intermediaries and optimizes ticket distribution, helping airlines decrease per-booking costs as NDC volume grows.',
          },
          {
            question: 'How does DRCT support ancillary sales, bundles, and personalized offers?',
            answer:
              'DRCT enhances NDC aggregator capabilities by enabling real-time bundles, ancillary upsells, and personalized pricing. We ensure these offers are consistently delivered across all channels, improving conversion and revenue per booking.',
          },
        ],
      },
    };
  },
  watch: {
    '$i18n.locale'() {
      this.updateDocumentMetadata();
    },
  },
  created() {
    this.updateDocumentMetadata();
  },
  methods: {
    localized(base, key) {
      return mergeIataAgenciesContent(base, airlinesTranslations[this.$i18n.locale]?.[key]);
    },
    updateDocumentMetadata() {
      const translation = airlinesTranslations[this.$i18n.locale];
      document.title =
        translation?.metaTitle ||
        'Airline Distribution Platform | NDC & GDS Integration for Airlines';
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute(
          'content',
          translation?.metaDescription ||
            'Optimize airline distribution with a unified platform combining NDC, GDS, and direct channels. Reduce costs, improve retailing, and scale your airline distribution strategy.'
        );
    },
  },
};
</script>
